/**
 * Maps Resources
 *
 * Provides access to @classic-homes/maps-core package documentation.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import mapsCatalog from '../data/maps-catalog.json' with { type: 'json' };
import type { MapsCatalog } from '../types.js';

const catalog = mapsCatalog as unknown as MapsCatalog;

/**
 * Register maps-related resources
 */
export function registerMapsResources(server: McpServer): void {
  // Full maps catalog
  server.resource('Maps Core Package', 'maps://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // Theme module
  server.resource('Maps Theme', 'maps://theme', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.theme.importPath,
            description: catalog.subpackages.theme.description,
            functions: catalog.subpackages.main.modules?.theme.functions,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Types
  server.resource('Maps Types', 'maps://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.types.importPath,
            description: 'TypeScript types exported from @classic-homes/maps-core',
            props: catalog.types.props,
            data: catalog.types.data,
            events: catalog.types.events,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Utilities (format + accessibility + styles)
  server.resource('Maps Utilities', 'maps://utils', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.main.importPath,
            description: 'Utility functions for formatting, accessibility, and styling',
            format: catalog.subpackages.main.modules?.format,
            accessibility: catalog.subpackages.main.modules?.accessibility,
            styles: catalog.subpackages.main.modules?.styles,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Components list
  server.resource('Maps Components', 'maps://components', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.components, null, 2),
      },
    ],
  }));

  // Quick reference
  server.resource('Maps Quick Reference', 'maps://quick-reference', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'text/markdown',
        text: `# Maps Core Quick Reference

## Installation

\`\`\`bash
${catalog.quickReference.installation}
\`\`\`

## Basic Usage

\`\`\`typescript
${catalog.quickReference.basicUsage}
\`\`\`

## Peer Dependency

\`${catalog.quickReference.peerDependency}\`

## Optional Peer Dependencies (for Deck.gl)

\`${catalog.quickReference.optionalPeerDependencies}\`

## Available Components

**React:** \`@classic-homes/maps-react\`
- ${catalog.components.react.components.join(', ')}

**Svelte:** \`@classic-homes/maps-svelte\`
- ${catalog.components.svelte.components.join(', ')}

## Theme Exports

- \`generateLightTheme()\` - Generate light theme from tokens
- \`generateDarkTheme()\` - Generate dark theme from tokens
- \`lightTheme\` - Pre-generated light theme
- \`darkTheme\` - Pre-generated dark theme
- \`lightBasicStyle\` - Pre-generated light MapLibre style
- \`darkBasicStyle\` - Pre-generated dark MapLibre style
- \`THEME_LIGHT\` - Constant: 'classic-light'
- \`THEME_DARK\` - Constant: 'classic-dark'

## Layer Types

- **FillLayer** - Polygon fills (choropleth, areas)
- **LineLayer** - Lines (routes, borders)
- **CircleLayer** - Point circles
- **SymbolLayer** - Icons and text labels
- **FillExtrusionLayer** - 3D building extrusions
- **HeatmapLayer** - Point density visualization
- **DeckOverlay** - Deck.gl integration for advanced WebGL

## Formatting Utilities

- \`formatCoordinates(coordinates, precision?, format?)\` - Format coordinates (decimal, DMS, or DD)
- \`formatDistance(meters, unit?)\` - Format distance (metric or imperial)
- \`formatArea(sqMeters, unit?)\` - Format area (metric or imperial)
- \`formatBounds(bounds)\` - Format bounding box

## Accessibility Utilities

- \`generateMapDescription(title, options?)\` - Accessible map description
- \`generateFeatureCollectionDescription(collection, name?)\` - Describe features
- \`KEYBOARD_CONTROLS\` - Keyboard shortcut descriptions

## Styling Utilities

- \`createHoverExpression(baseColor, hoverColor)\` - Hover state expression
- \`createSelectionExpression(baseColor, selectedColor)\` - Selection expression
- \`getHighlightStyle(theme)\` - Highlight style properties
- \`getSelectionStyle(theme)\` - Selection style properties

## React Hooks

- \`useMap()\` - Access map instance, state, and positioningCoordinator from context
- \`useMapInstance()\` - Get map instance when loaded (returns null otherwise)
- \`useMapTheme(theme?)\` - Get resolved theme, map theme object, and style
- \`useReducedMotion()\` - Detect user's motion preference
- \`useLayer(options)\` - Add custom layers with automatic lifecycle
- \`useControlPosition(ref, options)\` - Register controls with positioning coordinator
- \`useCustomControl()\` - Access CustomControl context from child components

## CSS Import Order

\`\`\`css
@import '@classic-homes/theme-styles';
@import 'maplibre-gl/dist/maplibre-gl.css';
@import '@classic-homes/maps-core/styles/controls.css';
@import '@classic-homes/maps-core/styles/popup.css';
@import '@classic-homes/maps-core/styles/basemap-switcher.css';

@tailwind base;
@tailwind components;
@tailwind utilities;
\`\`\`
`,
      },
    ],
  }));

  // Props types only
  server.resource('Maps Props Types', 'maps://props', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.props, null, 2),
      },
    ],
  }));

  // Data types only
  server.resource('Maps Data Types', 'maps://data', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.data, null, 2),
      },
    ],
  }));

  // Event types only
  server.resource('Maps Event Types', 'maps://events', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.types.events, null, 2),
      },
    ],
  }));

  // React hooks
  server.resource('Maps React Hooks', 'maps://hooks', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            package: catalog.components.react.package,
            hooks: catalog.components.react.hooks,
            context: catalog.components.react.context,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Best practices
  server.resource('Maps Best Practices', 'maps://best-practices', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.bestPractices, null, 2),
      },
    ],
  }));

  // Theme types
  server.resource('Maps Theme Types', 'maps://theme-types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog.themeTypes, null, 2),
      },
    ],
  }));

  // Overlay layers (LayerManager)
  server.resource('Maps Overlay Layers', 'maps://overlay-layers', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description:
              catalog.types.overlayLayers?.description ?? 'Overlay layer system for LayerManager',
            types: catalog.types.overlayLayers?.types ?? [],
            utilities: catalog.types.overlayLayers?.utilities ?? {},
            cssImport:
              catalog.types.overlayLayers?.cssImport ??
              "@import '@classic-homes/maps-core/styles/layer-manager.css';",
            example: catalog.types.overlayLayers?.example ?? '',
          },
          null,
          2
        ),
      },
    ],
  }));

  // Migration guide (extracted from best practices)
  server.resource('Maps Migration Guide', 'maps://migration', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'text/markdown',
        text: `# Migrating from Mapbox GL to Classic Theme Maps

## Key Differences

${catalog.bestPractices?.mapboxMigration.differences?.map((d) => `- ${d}`).join('\n')}

## Example Migration

\`\`\`typescript
${catalog.bestPractices?.mapboxMigration.exampleMigration}
\`\`\`

## CSS Setup

\`\`\`css
${catalog.quickReference.cssImportOrder}
\`\`\`

## Tailwind Configuration

\`\`\`javascript
${catalog.quickReference.tailwindConfig}
\`\`\`
`,
      },
    ],
  }));

  // Positioning coordinator system
  server.resource('Maps Positioning', 'maps://positioning', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description:
              'Centralized positioning coordinator for map controls with collision detection, priority-based resolution, and automatic z-index management',
            class: 'PositioningCoordinator',
            factory: 'createPositioningCoordinator',
            module: catalog.subpackages.main.modules?.positioning,
            types: catalog.types.positioning,
            hooks: {
              react: {
                name: 'useControlPosition',
                description:
                  'Hook for registering controls with the positioning coordinator. Automatically handles dimensions via ResizeObserver, registration lifecycle, and reactive position updates.',
                signature:
                  '(ref: RefObject<HTMLElement | null>, options: UseControlPositionOptions) => UseControlPositionResult',
                returns:
                  '{ offset: ControlOffset | null, setExpanded: (isExpanded: boolean) => void, style: React.CSSProperties, visible: boolean }',
              },
              svelte: {
                name: 'useControlPosition',
                description:
                  'Composable for registering controls with the positioning coordinator. Uses Svelte 5 reactivity for automatic dimension tracking and position updates.',
                signature:
                  '(getElement: () => HTMLElement | undefined, options: UseControlPositionOptions) => UseControlPositionResult',
                returns:
                  '{ readonly offset: ControlOffset | null, setExpanded: (isExpanded: boolean) => void, readonly style: string, readonly visible: boolean, readonly hasCoordinator: boolean }',
              },
            },
          },
          null,
          2
        ),
      },
    ],
  }));
}
